/**
 * Fuente única de verdad para datos del estudio.
 * Actualizar aquí propaga a: schema.js, Navbar, Footer, ContactInfo,
 * páginas legales y cualquier otro consumidor.
 *
 * ESM puro (sin imports de Next) para poder consumirlo desde cualquier capa.
 */

export const company = {
  name: 'Gómez & Thomas',
  shortName: 'G&T',
  legalName: 'Gómez & Thomas Abogados',
  tagline: 'Asesoría legal estratégica para empresas y familias.',
  city: 'Santiago',
  country: 'Chile',
  countryCode: 'CL',

  // TODO: completar con la dirección real de la oficina en Santiago.
  address: {
    street: '',
    locality: '',
    region: 'Región Metropolitana',
    country: 'Chile',
    countryCode: 'CL',
    postalCode: '',
    display: 'Santiago de Chile',
  },

  emails: {
    contacto: 'contacto@gomezthomas.cl',
    francisca: 'francisca@gomezthomas.cl',
    ines: 'ines@gomezthomas.cl',
    ilana: 'ilana.gelerstein@gomezthomas.cl',
    catalina: 'catalina@gomezthomas.cl',
  },

  phones: [
    {
      label: 'María Inés Thomas',
      tel: '+56974505401',
      display: '+56 9 7450 5401',
    },
    { label: 'Contacto', tel: '+56926077831', display: '+56 9 2607 7831' },
  ],

  // Click-to-chat directo (número de María Inés Thomas) con mensaje
  // predefinido redactado desde la vista del cliente.
  whatsapp:
    'https://wa.me/56974505401?text=Hola%2C%20me%20gustar%C3%ADa%20recibir%20asesor%C3%ADa%20legal%20de%20G%C3%B3mez%20%26%20Thomas.',
  whatsappNumber: '+56974505401',

  social: [
    {
      id: 'linkedin',
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/company/g-t-abogados/',
      handle: '/g-t-abogados',
    },
  ],

  languages: ['es', 'en'],
  areaServed: ['CL'],
  founded: '',

  docIds: {
    privacidad: 'DOC-LEGAL-001',
    terminos: 'DOC-LEGAL-002',
    version: '1.0',
    // TODO: fijar fecha de vigencia al publicar las páginas legales.
    vigencia: '',
  },
};

/**
 * Equipo del estudio. Fuente única para /equipo, /equipo/[slug] y el schema
 * Attorney (E-E-A-T: alumniOf, hasCredential, knowsAbout, sameAs).
 *
 * `tier`: 'socia' | 'asociado' | 'of-counsel' (orden de render y agrupación).
 * `bioShort`: una frase para la grilla. `bio`: párrafos para el perfil.
 * `credentials`: para el schema — puede incluir detalles verificables que la
 * bio visible no menciona (ej. distinciones). Verificar todo contra fuente
 * primaria antes de publicar.
 */
export const team = [
  {
    id: 'francisca-gomez',
    slug: 'francisca-gomez',
    tier: 'socia',
    founder: true,
    name: 'Francisca Gómez Lagos',
    role: 'Socia',
    email: 'francisca@gomezthomas.cl',
    linkedin:
      'https://www.linkedin.com/in/francisca-g%C3%B3mez-lagos-81b763a9/',
    languages: ['Español', 'Inglés'],
    photo: '/assets/people/francisca-gomez.webp',
    bioShort:
      'Asesora a empresas, familias y organizaciones sin fines de lucro en materias tributarias, corporativas y de planificación patrimonial.',
    bio: [
      'Francisca asesora a empresas, empresarios, familias y organizaciones sin fines de lucro en materias tributarias, corporativas y patrimoniales. Su práctica se centra en el diseño de estructuras jurídicas y tributarias eficientes, reorganizaciones empresariales, planificación patrimonial y asesoría permanente, acompañando a sus clientes en la toma de decisiones estratégicas con una visión integral del negocio.',
      'Cuenta con amplia experiencia asesorando a fundaciones, corporaciones y asociaciones gremiales en materias tributarias y de cumplimiento, apoyando procesos de constitución, reorganización, revisión de estructuras, cumplimiento de obligaciones tributarias y fortalecimiento de su institucionalidad.',
    ],
    formacionAcademica: [
      'Abogada, Pontificia Universidad Católica de Chile.',
      'Diplomado en Análisis y Planificación Tributaria, Pontificia Universidad Católica de Chile.',
    ],
    experiencia: [
      'Ha desarrollado su carrera profesional en PricewaterhouseCoopers (PwC), Garrigues y Crowe, acumulando más de diez años de experiencia en asesoría tributaria y corporativa. Ha participado en procesos de reorganización empresarial, planificación tributaria, evaluación de contingencias fiscales, estructuración de inversiones y asesoría permanente a empresas de diversos sectores.',
      'Asimismo, ha asesorado a fundaciones, corporaciones y otras organizaciones sin fines de lucro en materias tributarias y corporativas, contribuyendo al adecuado cumplimiento de sus obligaciones legales y al fortalecimiento de su estructura institucional.',
    ],
    areasPractica: [
      'Derecho Tributario',
      'Planificación Tributaria',
      'Reorganizaciones Empresariales',
      'Derecho Corporativo',
      'Empresas Familiares',
      'Planificación Patrimonial y Sucesoria',
      'Fundaciones, Corporaciones y Asociaciones Gremiales',
      'Organizaciones sin Fines de Lucro',
      'Asesoría Permanente a Empresas',
    ],
    alumniOf: ['Pontificia Universidad Católica de Chile'],
    credentials: [
      'Abogada, Pontificia Universidad Católica de Chile — titulada con dos votos de distinción (2015)',
      'Diplomado en Análisis y Planificación Tributaria, Pontificia Universidad Católica de Chile — distinción máxima (2017)',
    ],
    knowsAbout: [
      'Derecho tributario',
      'Planificación tributaria',
      'Reorganizaciones empresariales',
      'Derecho corporativo',
      'Planificación patrimonial y sucesoria',
      'Organizaciones sin fines de lucro',
    ],
  },
  {
    id: 'maria-ines-thomas',
    slug: 'maria-ines-thomas',
    tier: 'socia',
    founder: true,
    name: 'María Inés Thomas Ruiz',
    role: 'Socia',
    email: 'ines@gomezthomas.cl',
    linkedin: 'https://www.linkedin.com/in/mar%C3%ADain%C3%A9sthomasruiz/',
    languages: ['Español', 'Inglés'],
    photo: '/assets/people/maria-ines-thomas.webp',
    bioShort:
      'Asesora a empresas, organizaciones y familias en derecho corporativo, compliance, laboral, gobierno corporativo y gestión de riesgos.',
    bio: [
      'María Inés asesora a empresas, organizaciones y familias en materias de derecho corporativo, compliance, derecho laboral, recursos humanos, gobierno corporativo y gestión de riesgos. Su práctica se caracteriza por un enfoque estratégico y preventivo, acompañando a sus clientes en la toma de decisiones, la resolución de conflictos y la implementación de estructuras jurídicas que aporten seguridad y continuidad a sus negocios.',
      'Cuenta con una amplia experiencia en asesoría permanente a empresas, apoyando a directorios, gerencias generales y áreas de recursos humanos en materias laborales, contratación y desvinculación de trabajadores, negociaciones, elaboración de políticas internas, investigaciones laborales, procedimientos derivados de la Ley Karin, auditorías laborales y gestión de contingencias, privilegiando siempre soluciones prácticas que resguarden tanto a las personas como a la organización.',
      'Desde 2018 se ha especializado en el diseño e implementación de Modelos de Prevención de Delitos y programas de compliance, liderando procesos de levantamiento de riesgos, elaboración de matrices de riesgo, códigos de ética, canales de denuncia, investigaciones internas y fortalecimiento del gobierno corporativo para empresas de diversos sectores.',
      'Asimismo, asesora a empresas e inversionistas en la estructuración jurídica de operaciones inmobiliarias, compraventas de bienes raíces, desarrollo de proyectos y negociación de acuerdos comerciales y corporativos, buscando siempre soluciones eficientes que permitan compatibilizar los objetivos del negocio con una adecuada mitigación de riesgos. Su experiencia en negociación y resolución estratégica de conflictos le ha permitido acompañar exitosamente a empresas y organizaciones en procesos complejos, privilegiando acuerdos sostenibles y relaciones de largo plazo.',
    ],
    formacionAcademica: [
      'Abogada, Universidad Adolfo Ibáñez.',
      'Diplomado en Compliance Corporativo, Universidad Adolfo Ibáñez.',
      'Diplomado en Responsabilidad Social Empresarial, Universidad de Valencia, España.',
      'Becaria Fundación Étnor, España.',
    ],
    experiencia: [
      'Cuenta con experiencia en estudios jurídicos y en la consultora Crowe. Posee más de diez años de experiencia asesorando a empresas y organizaciones en materias corporativas, laborales, compliance y gobierno corporativo, participando en la implementación de programas de cumplimiento, investigaciones internas, asesoría estratégica a áreas de recursos humanos y negociación de acuerdos.',
    ],
    areasPractica: [
      'Compliance y Programas de Cumplimiento',
      'Modelos de Prevención de Delitos',
      'Derecho Laboral para Empresas',
      'Asesoría a Recursos Humanos',
      'Investigaciones Internas y Ley Karin',
      'Gobierno Corporativo',
      'Gestión de Riesgos',
      'Derecho Corporativo',
      'Negociación y Resolución de Conflictos',
      'Derecho Inmobiliario',
      'Estructuración y Negociación de Operaciones Inmobiliarias',
      'Organizaciones sin Fines de Lucro',
      'Ética Empresarial',
    ],
    alumniOf: ['Universidad Adolfo Ibáñez', 'Universidad de Valencia'],
    credentials: [
      'Abogada, Universidad Adolfo Ibáñez (2012)',
      'Diplomado en Compliance Corporativo, Universidad Adolfo Ibáñez (2017)',
      'Diplomado en Responsabilidad Social Empresarial, Universidad de Valencia, España (2014)',
      'Becaria Fundación Étnor, España',
    ],
    knowsAbout: [
      'Compliance corporativo',
      'Modelos de prevención de delitos',
      'Derecho laboral',
      'Gobierno corporativo',
      'Gestión de riesgos',
      'Derecho inmobiliario',
    ],
  },
  {
    id: 'ilana-gelerstein',
    slug: 'ilana-gelerstein',
    tier: 'socia',
    name: 'Ilana Gelerstein Waisbein',
    role: 'Socia',
    email: 'ilana.gelerstein@gomezthomas.cl',
    linkedin: 'https://www.linkedin.com/in/ilanagelerstein/',
    languages: ['Español', 'Inglés', 'Hebreo'],
    photo: '/assets/people/ilana-gelerstein.webp',
    bioShort:
      'Asesora a empresas y family offices en materias corporativas y tributarias, con foco en reorganizaciones y planificación patrimonial.',
    bio: [
      'Ilana asesora a empresas de distintos rubros, family offices y personas naturales en materias corporativas y tributarias, con especial foco en reorganizaciones empresariales, planificación tributaria y patrimonial, negociación de contratos y asesoría corporativa.',
      'Actualmente su práctica se concentra en la asesoría integral y permanente a empresas y personas naturales. Asesora a accionistas, directorios y equipos ejecutivos en materias de gobierno corporativo, cumplimiento de obligaciones corporativas y tributarias, análisis de contingencias y procesos de fiscalización.',
      'Acompaña a sus clientes tanto en su gestión habitual como en operaciones y decisiones estratégicas relacionadas con su estructura corporativa, inversiones, crecimiento y patrimonio. Su trabajo combina una mirada corporativa y tributaria, orientada a anticipar los efectos legales y fiscales de cada operación, prevenir contingencias y diseñar estructuras jurídicamente sólidas y eficientes en el tiempo.',
    ],
    formacionAcademica: [
      'Abogada, Pontificia Universidad Católica de Chile.',
      'Diplomado en Análisis y Planificación Tributaria, Pontificia Universidad Católica de Chile.',
      'Coordinadora y alumna del Curso de Nueva Justicia Tributaria y Aduanera, Pontificia Universidad Católica de Chile (2017).',
    ],
    experiencia: [
      'Desarrolló su carrera profesional en PwC Chile, donde durante más de ocho años participó en el diseño e implementación de reorganizaciones empresariales con efectos en Chile y en otras jurisdicciones, estructuración de inversiones nacionales e internacionales, operaciones societarias y negociación y revisión de contratos comerciales y corporativos.',
      'Su práctica actual se concentra en la asesoría permanente a empresas y personas naturales, acompañando a accionistas, directorios y equipos ejecutivos en gobierno corporativo, cumplimiento de obligaciones corporativas y tributarias, análisis de contingencias y procesos de fiscalización.',
    ],
    areasPractica: [
      'Derecho Corporativo',
      'Derecho Tributario',
      'Asesoría Permanente a Empresas',
      'Reorganizaciones Empresariales',
      'Negociación de Contratos Comerciales y Corporativos',
      'Gobierno Corporativo',
      'Empresas Familiares y Family Offices',
      'Planificación Patrimonial y Sucesoria',
      'Fiscalizaciones Tributarias',
      'Organizaciones sin Fines de Lucro',
    ],
    alumniOf: ['Pontificia Universidad Católica de Chile'],
    credentials: [
      'Abogada, Pontificia Universidad Católica de Chile',
      'Diplomado en Análisis y Planificación Tributaria, Pontificia Universidad Católica de Chile',
      'Coordinadora y alumna del Curso de Nueva Justicia Tributaria y Aduanera, Pontificia Universidad Católica de Chile (2017)',
    ],
    knowsAbout: [
      'Derecho corporativo',
      'Derecho tributario',
      'Reorganizaciones empresariales',
      'Planificación patrimonial y sucesoria',
      'Gobierno corporativo',
      'Fiscalizaciones tributarias',
    ],
  },
  {
    id: 'catalina-gomez',
    slug: 'catalina-gomez',
    tier: 'asociado',
    name: 'Catalina Gómez Moya',
    role: 'Abogada asociada',
    email: 'catalina@gomezthomas.cl',
    linkedin: 'https://www.linkedin.com/in/catalina-g%C3%B3mez-moya-a8a145356/',
    languages: ['Español'],
    photo: '/assets/people/catalina-gomez.webp',
    bioShort:
      'Participa en la elaboración de instrumentos jurídicos, la asesoría permanente a empresas y procedimientos judiciales y extrajudiciales.',
    bio: [
      'Catalina participa en la elaboración de instrumentos jurídicos, apoyo en asesoría permanente a empresas y gestión de procedimientos judiciales y extrajudiciales. Su práctica se caracteriza por un enfoque riguroso y orientado a entregar soluciones jurídicas oportunas y eficientes.',
    ],
    formacionAcademica: [
      'Abogada, Universidad de Talca.',
      'Ayudante de la cátedra de Historia Política y Constitucional de Chile, Universidad de Talca.',
      'Pasantía en el Centro de Mediación de Talca.',
      'Ha complementado su formación mediante cursos y programas de especialización en diversas áreas del derecho y competencias profesionales.',
    ],
    experiencia: [
      'Durante su formación profesional realizó una pasantía en el Centro de Mediación de Talca y posteriormente ha desarrollado experiencia en la tramitación de procedimientos judiciales y extrajudiciales, elaboración de contratos e instrumentos jurídicos y asesoría legal a empresas y personas, participando en proyectos corporativos y civiles junto al equipo del estudio.',
    ],
    areasPractica: [
      'Derecho Corporativo',
      'Derecho Laboral',
      'Derecho Civil',
      'Contratos',
      'Constitución y Modificación de Sociedades',
      'Asesoría Permanente a Empresas',
      'Procedimientos Judiciales y Extrajudiciales',
    ],
    alumniOf: ['Universidad de Talca'],
    credentials: ['Abogada, Universidad de Talca'],
    knowsAbout: [
      'Derecho corporativo',
      'Derecho laboral',
      'Derecho civil',
      'Contratos',
      'Procedimientos judiciales y extrajudiciales',
    ],
  },
  {
    id: 'antonio-garcia',
    slug: 'antonio-garcia',
    tier: 'asociado',
    name: 'Antonio García López',
    role: 'Abogado asociado',
    linkedin:
      'https://www.linkedin.com/in/antonio-de-jes%C3%BAs-garc%C3%ADa-6b20202b6/',
    languages: ['Español'],
    photo: '/assets/people/antonio-garcia.webp',
    bioShort:
      'Participa en asesorías tributarias, organizaciones patrimoniales, reestructuraciones societarias y resolución de contingencias fiscales.',
    bio: [
      'Antonio participa en nuestras asesorías tributarias, organizaciones patrimoniales, reestructuraciones societarias y materias corporativas, apoyando a los clientes en el cumplimiento de sus obligaciones tributarias y en la resolución de contingencias.',
    ],
    formacionAcademica: ['Abogado, Universidad Adolfo Ibáñez.'],
    experiencia: [
      'Durante su formación profesional realizó una pasantía en García, Nadal & Cía. y se desempeñó como procurador en las áreas tributaria y corporativa de Estudio Guzmán.',
    ],
    areasPractica: [
      'Derecho Tributario',
      'Derecho Corporativo',
      'Planificación Tributaria',
      'Reorganizaciones Empresariales',
      'Planificación Patrimonial',
      'Empresas Familiares',
      'Procedimientos ante el Servicio de Impuestos Internos',
      'Tribunales Tributarios y Aduaneros',
      'Asesoría Permanente a Empresas',
    ],
    alumniOf: ['Universidad Adolfo Ibáñez'],
    credentials: ['Abogado, Universidad Adolfo Ibáñez'],
    knowsAbout: [
      'Derecho tributario',
      'Planificación tributaria',
      'Reorganizaciones empresariales',
      'Derecho corporativo',
      'Procedimientos ante el Servicio de Impuestos Internos',
    ],
  },
  {
    id: 'tomas-reinero',
    slug: 'tomas-reinero',
    tier: 'of-counsel',
    name: 'Tomás Reinero Acevedo',
    role: 'Of counsel',
    linkedin:
      'https://www.linkedin.com/in/tom%C3%A1s-reinero-acevedo-1ba75a184/',
    languages: ['Español'],
    // TODO: foto provisional (baja resolución, de un PPT) — reemplazar con re-toma.
    photo: '/assets/people/tomas-reinero.webp',
    bioShort:
      'Asesora a empresas y personas en litigación civil y laboral, resolución estratégica de controversias y propiedad intelectual e industrial.',
    bio: [
      'Tomás asesora a empresas y personas en materias de derecho civil, litigación y propiedad intelectual. Su práctica se centra en la resolución estratégica de controversias civiles y laborales, representación judicial y extrajudicial, así como en la protección de activos intangibles y asesoría en propiedad industrial.',
      'Cuenta con experiencia en litigios civiles y laborales, acompañando a sus clientes en la definición de estrategias procesales, negociación de acuerdos y defensa de sus intereses ante tribunales de justicia. Asimismo, asesora en materias de propiedad intelectual e industrial, incluyendo protección de marcas y otros activos intangibles.',
    ],
    formacionAcademica: [
      'Abogado, Universidad Católica del Norte.',
      'Máster, Universidad de Barcelona.',
    ],
    experiencia: [],
    areasPractica: [
      'Litigación Civil',
      'Litigación Laboral',
      'Derecho Civil',
      'Propiedad Intelectual',
      'Propiedad Industrial',
      'Marcas Comerciales e INAPI',
      'Resolución de Controversias',
    ],
    alumniOf: ['Universidad Católica del Norte', 'Universidad de Barcelona'],
    credentials: [
      'Abogado, Universidad Católica del Norte',
      'Máster, Universidad de Barcelona',
    ],
    knowsAbout: [
      'Litigación civil',
      'Litigación laboral',
      'Derecho civil',
      'Propiedad intelectual e industrial',
      'Resolución de controversias',
    ],
  },
  {
    id: 'javiera-yanez',
    slug: 'javiera-yanez',
    tier: 'of-counsel',
    name: 'Javiera Yáñez González',
    role: 'Of counsel',
    linkedin: 'https://www.linkedin.com/in/javiera-y-431019143/',
    languages: ['Español', 'Inglés'],
    // TODO: foto provisional (baja resolución, de un PPT) — reemplazar con re-toma.
    photo: '/assets/people/javiera-yanez.webp',
    bioShort:
      'Asesora a empresas e inversionistas en materias corporativas, tributarias e inmobiliarias y en el desarrollo de proyectos de inversión.',
    bio: [
      'Javiera asesora a empresas e inversionistas en materias corporativas, tributarias e inmobiliarias, participando en el desarrollo y estructuración de proyectos de inversión, negociación y elaboración de contratos complejos y procesos de adquisición y desarrollo inmobiliario.',
      'Su experiencia comprende la asesoría integral en proyectos inmobiliarios, incluyendo procesos de adquisición, estructuración jurídica, financiamiento, desarrollo, construcción y comercialización, así como la elaboración y negociación de contratos comerciales y financieros. Asimismo, asesora a directorios y empresas en materias corporativas y tributarias, acompañando la toma de decisiones estratégicas durante todo el ciclo de vida de los proyectos.',
    ],
    formacionAcademica: [
      'Abogada, Pontificia Universidad Católica de Chile.',
      'Master of Law and Business, Bucerius Law School, con especialización en Emprendimiento.',
    ],
    experiencia: [
      'Cuenta con más de siete años de experiencia en asesoría corporativa y tributaria, estructuración de contratos financieros y comerciales, gobierno corporativo, asesoría a directorios y desarrollo de proyectos inmobiliarios.',
    ],
    areasPractica: [
      'Derecho Corporativo',
      'Derecho Tributario',
      'Derecho Inmobiliario',
      'Desarrollo de Proyectos Inmobiliarios',
      'Contratos Comerciales y Financieros',
      'Fusiones y Adquisiciones',
      'Gobierno Corporativo',
      'Asesoría a Directorios',
    ],
    alumniOf: [
      'Pontificia Universidad Católica de Chile',
      'Bucerius Law School',
    ],
    credentials: [
      'Abogada, Pontificia Universidad Católica de Chile',
      'Master of Law and Business, Bucerius Law School — especialización en Emprendimiento',
    ],
    knowsAbout: [
      'Derecho corporativo',
      'Derecho tributario',
      'Derecho inmobiliario',
      'Desarrollo de proyectos inmobiliarios',
      'Fusiones y adquisiciones',
    ],
  },
];

// Etiquetas de agrupación por tier (orden de render en /equipo).
export const teamTiers = [
  { id: 'socia', label: 'Socias' },
  { id: 'asociado', label: 'Abogados asociados' },
  { id: 'of-counsel', label: 'Of counsel' },
];

// Solo las socias — lo consumen Footer, contacto y el nodo LegalService.
export const partners = team.filter((t) => t.tier === 'socia');

export const getPerson = (slug) => team.find((t) => t.slug === slug);
